import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { Flag } from "lucide-react";
import type { FC } from "react";
import { edgeAwareTransform } from "web_myplann/Goals/Helpers/edgeAwareTransform";
import type { ExtraContributionImpact } from "web_myplann/Goals/Types/ExtraContributionImpact";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	impact: ExtraContributionImpact;
	savedLabel: string;
};

export const EarlierFinishViz: FC<Props> = ({ impact, savedLabel }) => {
	const theme = useTheme();
	const { t } = useResource();
	const accent = theme.palette.success.main;
	const markerTransform = edgeAwareTransform(impact.progressPercent);

	return (
		<Stack pt={1.2} gap={1.2}>
			<Box position="relative" height={44}>
				<Box
					position="absolute"
					left={0}
					right={0}
					top={8}
					height="1px"
					bgcolor={theme.palette.grey[200]}
				/>
				<Typography
					variant="hint"
					tabular
					position="absolute"
					left={0}
					top={18}
					sx={{
						opacity: impact.progressPercent < 20 ? 0 : 1,
						transition: "opacity 150ms ease-out",
					}}
				>
					{t(Resources.Goals.Detail.ExtraContribution.Earlier.today)}
				</Typography>
				<Typography
					variant="hint"
					tabular
					position="absolute"
					right={0}
					top={18}
					sx={{
						opacity: impact.progressPercent > 80 ? 0 : 1,
						transition: "opacity 150ms ease-out",
					}}
				>
					{impact.dueYear}
				</Typography>
				<Stack
					alignItems="center"
					position="absolute"
					left={`${impact.progressPercent}%`}
					top={-4}
					sx={{ transform: "translateX(-50%)" }}
				>
					<Flag size={theme.iconSize.sm} color={accent} fill={accent} />
					<Box width="1px" height={8} bgcolor={alpha(accent, 0.5)} />
				</Stack>
				<Typography
					variant="bodyStrong"
					color={accent}
					tabular
					noWrap
					position="absolute"
					top={18}
					left={`${impact.progressPercent}%`}
					sx={{ transform: markerTransform }}
				>
					{impact.newYear}
				</Typography>
			</Box>
			<Stack
				direction="row"
				alignSelf="center"
				alignItems="center"
				gap={1}
				px={1.2}
				py={0.6}
				bgcolor={alpha(accent, 0.1)}
				borderRadius={theme.radii.full}
			>
				<Typography variant="bodyStrong" color={accent} tabular>
					{savedLabel}
				</Typography>
				<Typography variant="caption" color={accent}>
					{t(Resources.Goals.Detail.ExtraContribution.Earlier.earlier)}
				</Typography>
			</Stack>
		</Stack>
	);
};
